/**
 * Automatic zigzag layout for page content
 * Organizes images and text blocks in an alternating left-right layout
 */

class PageLayout {
  constructor() {
    // Try both main-page and regular page containers
    this.rteContainer = document.querySelector('.main-page .rte') || 
                        document.querySelector('.page-width--narrow .rte') ||
                        document.querySelector('.rte');
    if (!this.rteContainer) return;
    
    this.init();
  }

  init() {
    // Only apply on desktop
    if (window.innerWidth < 750) return;
    
    this.organizeContent();
    
    // Reorganize on resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 750) {
          this.organizeContent();
        } else {
          this.resetLayout();
        }
      }, 250);
    });
  }

  organizeContent() {
    // Get fresh list of children
    const children = Array.from(this.rteContainer.children);
    if (children.length === 0) return;

    // Skip if already organized
    if (this.rteContainer.querySelector('.zigzag-block')) return;

    let i = 0;
    let isLeft = true; // Start with image on left

    while (i < children.length) {
      const current = children[i];
      if (!current || current.parentNode !== this.rteContainer) {
        i++;
        continue;
      }

      const next = children[i + 1];

      // Check if current is text content and next is image
      const currentIsText = this.isTextContent(current);
      const nextIsImage = next && this.isImageContent(next);

      if (currentIsText && nextIsImage) {
        // Create zigzag block
        const block = document.createElement('div');
        block.className = `zigzag-block zigzag-block--${isLeft ? 'left' : 'right'}`;
        
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'zigzag-block__content';
        
        // Collect all text content until we hit an image
        let textIndex = i;
        const textElements = [];
        while (textIndex < children.length && this.isTextContent(children[textIndex])) {
          textElements.push(children[textIndex]);
          textIndex++;
        }
        
        // Move text elements to content wrapper
        textElements.forEach(el => {
          if (el && el.parentNode === this.rteContainer) {
            contentWrapper.appendChild(el);
          }
        });
        
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'zigzag-block__image';
        
        // Collect all consecutive images
        let imageIndex = textIndex;
        const imageElements = [];
        while (imageIndex < children.length && this.isImageContent(children[imageIndex])) {
          imageElements.push(children[imageIndex]);
          imageIndex++;
        }
        
        // Move image elements to image wrapper
        imageElements.forEach(el => {
          if (el && el.parentNode === this.rteContainer) {
            imageWrapper.appendChild(el);
          }
        });

        // Only create block if we have both content and images
        if (textElements.length > 0 && imageElements.length > 0) {
          // Add wrappers to block in correct order
          if (isLeft) {
            block.appendChild(imageWrapper);
            block.appendChild(contentWrapper);
          } else {
            block.appendChild(contentWrapper);
            block.appendChild(imageWrapper);
          }

          // Insert block before the first text element
          this.rteContainer.insertBefore(block, textElements[0]);
          
          // Update index to skip processed elements
          i = imageIndex;
          isLeft = !isLeft;
        } else {
          i++;
        }
      } else {
        i++;
      }
    }
  }

  isTextContent(element) {
    if (!element) return false;
    
    const tagName = element.tagName;
    const isHeading = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tagName);
    const isParagraph = tagName === 'P';
    const isList = ['UL', 'OL'].includes(tagName);
    const isDiv = tagName === 'DIV' && !element.classList.contains('image-placeholder') && !element.querySelector('img, .image-placeholder');
    
    return isHeading || isParagraph || isList || isDiv;
  }

  isImageContent(element) {
    if (!element) return false;
    
    const tagName = element.tagName;
    const isImg = tagName === 'IMG';
    const isPlaceholder = element.classList.contains('image-placeholder');
    const hasImage = element.querySelector && element.querySelector('img, .image-placeholder');
    
    return isImg || isPlaceholder || hasImage;
  }

  resetLayout() {
    const zigzagBlocks = this.rteContainer.querySelectorAll('.zigzag-block');
    zigzagBlocks.forEach(block => {
      const contentWrapper = block.querySelector('.zigzag-block__content');
      const imageWrapper = block.querySelector('.zigzag-block__image');
      
      if (contentWrapper) {
        const contentChildren = Array.from(contentWrapper.children);
        contentChildren.forEach(child => {
          block.parentNode.insertBefore(child, block);
        });
      }
      
      if (imageWrapper) {
        const imageChildren = Array.from(imageWrapper.children);
        imageChildren.forEach(child => {
          block.parentNode.insertBefore(child, block);
        });
      }
      
      block.remove();
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PageLayout();
  });
} else {
  new PageLayout();
}

